<script setup lang="ts">
import { useGameStore } from '@/stores/gameStore'
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()

const timeRemainingPercentage = ref(0)
const now = ref(new Date())
let countdownInterval: number | undefined

const gameHasStarted = computed(() => gameStore.turn > 1)

// Calculate time left until arena starts
const timeLeft = computed(() => {
  const startTime = new Date(gameStore.startTime)
  const totalSeconds = Math.max(0, Math.floor((startTime.getTime() - now.value.getTime()) / 1000))

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const pad = (n: number) => n.toString().padStart(2, "0")

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
})

const updateTimeRemainingPercentage = () => {
  const timeSinceTurn = Date.now() / 1000 - gameStore.dayTurnedAt
  const percentage = timeSinceTurn / ((gameStore.dayInterval * 60) / 100)
  timeRemainingPercentage.value = Math.min(100, Math.max(0, percentage))
}

const showDayToolTip = () => {
  gameStore.modalType = 'dayWeek'
}

onMounted(() => {
  // Update immediately and then every second
  updateTimeRemainingPercentage()
  const interval = setInterval(updateTimeRemainingPercentage, 1000)
  
  // Also update countdown timer if game hasn't started
  if (!gameHasStarted.value) {
    countdownInterval = window.setInterval(() => {
      now.value = new Date()
    }, 1000)
  }

  // Clear the interval when the component is unmounted
  onUnmounted(() => {
    clearInterval(interval)
    if (countdownInterval) {
      clearInterval(countdownInterval)
    }
  })
})
</script>

<template>
  <div class="flex">
    <div
      v-if="!gameHasStarted"
      class="relative me-1 overflow-hidden rounded-md cursor-pointer sm:min-w-28 bg-slate-600"
      @click="showDayToolTip"
    >
      <div class="p-1 ps-2 pe-2 relative z-10 text-center text-green-400 font-medium">
        {{ timeLeft }}
      </div>
    </div>
    
    <!-- Normal day progress bar for Day 2+ -->
    <div
      v-else
      class="relative me-1 overflow-hidden rounded-md cursor-pointer sm:min-w-28"
      @click="showDayToolTip"
    >
      <div class="bg-slate-600 absolute inset-0"></div>
      <div
        :class="gameStore.shipCommandBlock ? 'bg-blue-600' : 'bg-slate-800'"
        class="absolute inset-0"
        :style="{ width: timeRemainingPercentage + '%' }"
        :title="gameStore.shipCommandBlock ? t('game.time.commandsBlocked') : ''"
      ></div>
      <div class="p-1 ps-2 pe-2 relative z-10 text-center">{{ t('game.time.day', { turn: gameStore.turn }) }}</div>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>